import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { Provider as JotaiProvider } from 'jotai';
import { Layout } from '@/components/Layout';
import { ExecutionsView } from '@/pages/ExecutionsView';
import { GlobalRevenueView } from '@/pages/GlobalRevenueView';
import { RevenueLayout } from '@/pages/RevenueLayout';
import { ConsolidatedRevenueView } from '@/pages/ConsolidatedRevenueView';
import { GeoRevenueView } from '@/pages/GeoRevenueView';
import { RevenueGoalsView } from '@/pages/RevenueGoalsView';
import { CampaignDetailView } from '@/pages/CampaignDetailView';
import { ReportDashboardView } from '@/pages/ReportDashboardView';
import { AuthCallback } from '@/components/AuthCallback';
import { ProtectedRoute } from '@/components/ProtectedRoute';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <JotaiProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Routes>
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/executions" replace />} />
              <Route path="executions" element={<ExecutionsView />} />
              <Route path="campaigns/:executionId" element={<CampaignDetailView />} />
              <Route path="campaigns/:executionId/report" element={<ReportDashboardView />} />
              <Route path="revenue" element={<RevenueLayout />}>
                <Route index element={<ConsolidatedRevenueView />} />
                <Route
                  path="display-video"
                  element={<GlobalRevenueView key="display-video" scope="display-video" />}
                />
                <Route
                  path="chatgpt-ads"
                  element={<GlobalRevenueView key="chatgpt-ads" scope="chatgpt-ads" />}
                />
                <Route path="geo" element={<GeoRevenueView />} />
                <Route path="goals" element={<RevenueGoalsView />} />
              </Route>
              <Route path="global-revenue" element={<Navigate to="/revenue" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
      <Toaster
        position="bottom-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'var(--surface-raised)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-gray)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--elevation-3)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-body-m)',
          },
          success: {
            iconTheme: {
              primary: 'var(--success)',
              secondary: 'var(--brand-ivory)',
            },
          },
          error: {
            iconTheme: {
              primary: 'var(--warning)',
              secondary: 'var(--brand-ivory)',
            },
          },
        }}
      />
      </QueryClientProvider>
    </JotaiProvider>
  );
}

export default App;
